import React, { useState } from "react";
import { UserIcon, EnvelopeIcon, PhoneIcon, CalendarIcon, ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { PencilIcon, CheckIcon, XIcon } from "lucide-react";
import { formatDateOnly } from "../../../../utils/dateFormatter";
import api from "../../../../middleware/api";
import UnifiedPhoneInput from "../../../Common/Inputs/UnifiedPhoneInput/UnifiedPhoneInput";
import { formatEntityPhone } from "../../../../utils/phoneDisplay.utils";

const PersonalInfoCard = ({ user }) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [isEditingPhone, setIsEditingPhone] = useState(false);
    const [phoneValue, setPhoneValue] = useState(user.phone || '');
    const [phonePrefixValue, setPhonePrefixValue] = useState(user.phonePrefix || '+33');
    const [isUpdating, setIsUpdating] = useState(false);
    const [validationError, setValidationError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const toggleExpansion = () => {
        setIsExpanded(!isExpanded);
    };



    const handleEditPhone = () => {
        setIsEditingPhone(true);
        setPhoneValue(user.phone || '');
        setPhonePrefixValue(user.phonePrefix || '+33');
        setValidationError('');
        setSuccessMessage('');
    };

    const handleSavePhone = async () => {
        const trimmedPhone = phoneValue.trim();

        setIsUpdating(true);
        setValidationError('');

        try {
            await api.put(`/users/update/${user.id}`, { 
                phone: trimmedPhone,
                phonePrefix: phonePrefixValue
            });

            // Mise à jour locale de l'objet user
            user.phone = trimmedPhone;
            user.phonePrefix = phonePrefixValue;

            setIsEditingPhone(false);
            setSuccessMessage('Téléphone mis à jour avec succès');

            // Effacer le message de succès après 3 secondes
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (error) {
            if (error.response?.status === 403) {
                setValidationError('Vous n\'avez pas la permission de modifier ce profil');
            } else if (error.response?.status === 400) {
                setValidationError('Format de téléphone invalide');
            } else {
                setValidationError('Une erreur est survenue, veuillez réessayer');
            }
        } finally {
            setIsUpdating(false);
        }
    };

    const handleCancelEdit = () => {
        setIsEditingPhone(false);
        setPhoneValue(user.phone || '');
        setPhonePrefixValue(user.phonePrefix || '+33');
        setValidationError('');
        setSuccessMessage('');
    };

    const handlePhoneInputChange = (event) => {
        const { name, value } = event.target;
        if (name === 'phone') {
            setPhoneValue(value);
        } else if (name === 'phonePrefix') {
            setPhonePrefixValue(value);
        }
    };

    return (
        <section aria-labelledby="personal-info-title" className="bg-white rounded-lg shadow-md p-4 flex flex-col">
            <div
                className="flex justify-between items-center cursor-pointer"
                onClick={toggleExpansion}
            >
                <h2 id="personal-info-title" className="text-xl font-semibold flex items-center gap-2">
                    <UserIcon className="w-5 h-5 text-primary" />
                    Informations Personnelles
                </h2>

                {isExpanded ? (
                    <ChevronUpIcon className="w-5 h-5 text-gray-500" />
                ) : (
                    <ChevronDownIcon className="w-5 h-5 text-gray-500" />
                )}
            </div>
            <p className="text-xs text-base-content/70 mt-3">
                Personnalisez vos informations personnelles.
            </p>

            {isExpanded && (
                <div className="space-y-3 mt-3 flex-grow">
                    <div className="flex items-center gap-2">
                        <EnvelopeIcon className="w-4 h-4 text-gray-500" />
                        <div>
                            <p className="text-xs text-gray-500">Email</p>
                            <p className="font-medium text-sm">{user.email}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <PhoneIcon className="w-4 h-4 text-gray-500" />
                        <div className="flex-grow">
                            <p className="text-xs text-gray-500">Téléphone</p>
                            {isEditingPhone ? (
                                <div className="flex items-center gap-2 mt-1">
                                    <div className="flex-grow">
                                        <UnifiedPhoneInput
                                            name="phone"
                                            value={phoneValue}
                                            phonePrefix={phonePrefixValue}
                                            onChange={handlePhoneInputChange}
                                            placeholder="Téléphone"
                                            disabled={isUpdating}
                                            error={validationError}
                                            touched={true}
                                            size="sm"
                                            className="text-sm"
                                        />
                                    </div>
                                    <button
                                        onClick={handleSavePhone}
                                        disabled={isUpdating}
                                        className="p-1 text-green-600 hover:text-green-800 disabled:opacity-50"
                                        title="Sauvegarder"
                                    >
                                        <CheckIcon className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={handleCancelEdit}
                                        disabled={isUpdating}
                                        className="p-1 text-red-600 hover:text-red-800 disabled:opacity-50"
                                        title="Annuler"
                                    >
                                        <XIcon className="w-4 h-4" />
                                    </button>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <p className="font-medium text-sm">
                                        {formatEntityPhone(user)}
                                    </p>
                                    <button
                                        onClick={handleEditPhone}
                                        className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                        title="Modifier le téléphone"
                                    >
                                        <PencilIcon className="w-3 h-3" />
                                    </button>
                                </div>
                            )}
                            {successMessage && (
                                <p className="text-xs text-green-500 mt-1">{successMessage}</p>
                            )}
                            {isUpdating && (
                                <p className="text-xs text-blue-500 mt-1">Mise à jour en cours...</p>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <CalendarIcon className="w-4 h-4 text-gray-500" />
                        <div>
                            <p className="text-xs text-gray-500">Date de création</p>
                            <p className="font-medium text-sm">{formatDateOnly(user.creationDate)}</p>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default PersonalInfoCard; 